import { Activity, Layers, Pipette, Cpu, Zap, Columns2, Film } from "lucide-react";
import { DownloadsHeader } from "@/components/ManualLink";
import { BinaryGrid } from "@/components/BinaryGrid";
import { DenoiseStill } from "@/components/DenoiseStill";


const FEATURES = [
  { icon: Activity, title: "Smarter motion", body: "Quarter-pixel motion vectors on 8×8 blocks, outlier-free vector smoothing and overlapped-block compensation with no seams. Pixels that don't match are left out, so there's no ghosting. It measures about +0.8 dB over v1.1 on fast pans and handheld shake." },
  { icon: Layers, title: "Wavelet spatial, luma + chroma", body: "Five-scale à-trous wavelet shrinkage with separate Luma NR and Chroma NR, so colour blotches go without plastic skin." },
  { icon: Pipette, title: "Noise profile picker", body: "Drag a box over a flat area and click Analyze. The plugin measures the clip's real noise and tunes every scale to it. Auto mode re-estimates per frame." },
  { icon: Cpu, title: "Native GPU · ACES ready", body: "Runs right inside Resolve's own GPU memory (Metal or OpenCL), so frames never round-trip through the CPU. CUDA on NVIDIA, with an automatic fallback to all CPU cores. Every engine runs the same math, fully offline. Works in Rec.709, ACEScct, ACEScc, ACEScg, ACES2065-1 and DaVinci Wide Gamut projects." },
  { icon: Zap, title: "Fast playback", body: "Frames and motion stay cached on the GPU, so during playback each frame only reads one new neighbour and starts its motion search from the last frame. Switch Quality to Fast playback for a lighter, 1-frame-each-side mode while you scrub." },
  { icon: Columns2, title: "Split view", body: "Drag a divider in the viewer to compare the original and the denoised image side by side, with an optional thin guide line." },
  { icon: Film, title: "Grain control", body: "Keep part of the clip's own grain, or add animated film grain with size, amount and softness. Clean, but never plastic." },
];


export const DenoiseSection = ({ bins, dl, rebuild }) => (
  <section id="denoise" className="scroll-mt-6 card p-6 space-y-6" data-testid="denoise-section">
    <div className="flex items-start gap-3 flex-wrap">
      <div>
        <div className="chip mb-3 text-[#00e5ff] border-[#00e5ff]">new · v1.3 · Metal · CUDA · OpenCL</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">
          KolorKlinic <span className="text-[#00e5ff]">Denoise</span>
        </h2>
        <p className="mt-3 text-sm md:text-base text-[#9ba3b4] max-w-2xl leading-relaxed">
          High-end noise reduction OFX for DaVinci Resolve. It combines temporal and spatial
          denoising and keeps the texture. On our test footage it removed about 16 dB of noise
          (PSNR 24.4 → 40.4 dB).
        </p>
      </div>
    </div>

    <DenoiseStill />

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {FEATURES.map(({ icon: Icon, title, body }) => (
        <div key={title} className="demo-card bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`denoise-feature-${title.split(" ")[0].toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-1">
            <Icon size={16} className="text-[#00e5ff]" />
            <h3 className="font-display uppercase text-base">{title}</h3>
          </div>
          <p className="text-sm text-[#9ba3b4] leading-relaxed">{body}</p>
        </div>
      ))}
    </div>

    <div>
      <DownloadsHeader slug="Denoise" name="Denoise" color={"#00e5ff"} testid="denoise-manual-download-btn" />
      <BinaryGrid prefix="denoise-" bins={bins} dl={dl} rebuild={rebuild} />
    </div>
  </section>
);

const PRODUCTS = [
  { href: "#texture", name: "Texture", tag: "Film texture · €99", color: "#ff6a3d", body: "Spatial equalizer, halation, diffusion and film grain for an organic, filmic image.", testid: "product-texture" },
  { href: "#reactor", name: "Reactor", tag: "Film colour · €129", color: "#c48bff", body: "Film colour look development with the full Texture engine built in.", testid: "product-reactor" },
  { href: "#skin", name: "Skin", tag: "Beauty retouch · €79", color: "#ff7a9c", body: "Frequency-separation retouch with an automatic skin mask and no flicker.", testid: "product-skin" },
  { href: "#denoise", name: "Denoise", tag: "GPU noise reduction · offline", color: "#00e5ff", body: "Motion-compensated temporal and wavelet NR with split view and grain control.", testid: "product-denoise" },
  { href: "/ai#gen-alpha", name: "Gen Alpha", tag: "AI matte · €99", color: "#e69d27", body: "AI character matte for whole clips. Bring your own fal.ai key, no subscription.", testid: "product-gen-alpha" },
  { href: "/ai#recast", name: "Recast", tag: "Performance fix · €99", color: "#4ff2c2", body: "Fix the take without a reshoot: smile, blinks, gaze and head pose, or retarget a performance.", testid: "product-recast" },
];

export const ProductStrip = () => (
  <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4" data-testid="product-strip">
    {PRODUCTS.map((p) => (
      <a key={p.name} href={p.href} data-testid={p.testid}
        className="demo-card group card p-5 flex flex-col gap-3 border-l-2" style={{ borderLeftColor: p.color }}>
        <div className="flex-1">
          <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: p.color }}>{p.tag}</div>
          <div className="font-display uppercase text-2xl mt-1">KolorKlinic {p.name}</div>
          <p className="text-sm text-[#9ba3b4] mt-1">{p.body}</p>
        </div>
        <span className="font-mono text-xs uppercase tracking-widest mt-auto transition-transform group-hover:translate-x-1" style={{ color: p.color }}>
          view &amp; download →
        </span>
      </a>
    ))}
  </section>
);
